import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

export interface AIResponse {
  text: string;
  confidence: number;
  intent: string;
  entities: any[];
  actions?: string[];
  suggestions?: string[];
}

export interface ChatContext {
  messages: Array<{
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: Date;
  }>;
  userContext: {
    currentPage?: string;
    recentActions?: string[];
    userPreferences?: any;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AIChatbotService {
  private readonly apiUrl = environment.aiApiUrl || 'https://api.openai.com/v1/chat/completions';
  private readonly apiKey = environment.aiApiKey;
  private readonly maxRetries = 3;
  private readonly timeoutMs = 30000;

  constructor(private http: HttpClient) {}

  /**
   * Send message to AI service and get intelligent response
   */
  async getAIResponse(
    userMessage: string,
    context: ChatContext,
    options: {
      useFallback?: boolean;
      temperature?: number;
      maxTokens?: number;
    } = {}
  ): Promise<AIResponse> {
    const { useFallback = true, temperature = 0.7, maxTokens = 500 } = options;

    try {
      // Try AI service first
      const aiResponse = await this.callAIService(userMessage, context, temperature, maxTokens);
      return aiResponse;
    } catch (error) {
      console.warn('AI service failed, using fallback:', error);

      if (useFallback) {
        return this.getFallbackResponse(userMessage, context);
      }

      throw error;
    }
  }

      /**
   * Call external AI service (OpenAI, Azure, etc.)
   */
  private async callAIService(
    userMessage: string,
    context: ChatContext,
    temperature: number,
    maxTokens: number
  ): Promise<AIResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.apiKey}`
    });

    const systemPrompt = this.buildSystemPrompt(context);
    const messages = [
      { role: 'system', content: systemPrompt },
      ...context.messages.slice(-10), // Last 10 messages for context
      { role: 'user', content: userMessage }
    ];

    const payload = {
      model: 'gpt-3.5-turbo',
      messages,
      temperature,
      max_tokens: maxTokens,
      top_p: 1,
      frequency_penalty: 0,
      presence_penalty: 0
    };

    return new Promise<AIResponse>((resolve, reject) => {
      this.http.post<any>(this.apiUrl, payload, { headers })
        .pipe(
          timeout(this.timeoutMs),
          map(response => this.parseAIResponse(response, userMessage)),
          catchError(error => {
            console.error('AI API Error:', error);
            reject(error);
            return throwError(() => error);
          })
        )
        .subscribe({
          next: (response: AIResponse) => resolve(response),
          error: (error) => reject(error)
        });
    });
  }

  /**
   * Build system prompt with context
   */
  private buildSystemPrompt(context: ChatContext): string {
    return `You are Lugi, an AI assistant for a project management system.

Current context:
- User is on: ${context.userContext.currentPage || 'unknown page'}
- Recent actions: ${context.userContext.recentActions?.join(', ') || 'none'}

Your capabilities:
1. Navigate users to different pages (dashboard, projects, tasks, clients, etc.)
2. Help create new projects and tasks
3. Search and find existing projects
4. Answer questions about project management
5. Provide contextual help based on user's current location

Guidelines:
- Be conversational and helpful
- Provide specific, actionable responses
- If user wants to perform an action, include the action in your response
- Keep responses concise but informative
- If unsure, ask for clarification

Available actions:
- navigate_to_page: Navigate to a specific page
- open_form: Open add/edit forms
- search_project: Search for projects
- get_help: Provide contextual help
- create_item: Create new projects/tasks

Format your response as JSON with: text, confidence (0-1), intent, entities, actions, suggestions`;
  }

  /**
   * Parse AI service response
   */
  private parseAIResponse(response: any, userMessage: string): AIResponse {
    try {
      const content = response.choices?.[0]?.message?.content;
      if (!content) {
        throw new Error('Invalid AI response format');
      }

      // Try to parse as JSON first
      try {
        const parsed = JSON.parse(content);
        return {
          text: parsed.text || content,
          confidence: parsed.confidence || 0.8,
          intent: parsed.intent || 'general',
          entities: parsed.entities || [],
          actions: parsed.actions || [],
          suggestions: parsed.suggestions || []
        };
      } catch {
        // If not JSON, treat as plain text
        return {
          text: content,
          confidence: 0.7,
          intent: this.extractIntent(userMessage),
          entities: [],
          actions: [],
          suggestions: []
        };
      }
    } catch (error) {
      console.error('Error parsing AI response:', error);
      // Return a fallback response instead of throwing
      return {
        text: 'I apologize, but I encountered an issue processing your request. Let me help you with something else.',
        confidence: 0.3,
        intent: 'help',
        entities: [],
        actions: [],
        suggestions: ['Go to dashboard', 'Create project', 'Add task', 'Search projects']
      };
    }
  }

  /**
   * Extract intent from user message
   */
  private extractIntent(message: string): string {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('add') || lowerMessage.includes('create') || lowerMessage.includes('new')) {
      return 'create';
    }
    if (lowerMessage.includes('find') || lowerMessage.includes('search') || lowerMessage.includes('show')) {
      return 'search';
    }
    if (lowerMessage.includes('go to') || lowerMessage.includes('navigate') || lowerMessage.includes('open')) {
      return 'navigate';
    }
    if (lowerMessage.includes('help') || lowerMessage.includes('how') || lowerMessage.includes('what')) {
      return 'help';
    }

    return 'general';
  }

  /**
   * Fallback response when AI service is unavailable
   */
  private getFallbackResponse(userMessage: string, context: ChatContext): AIResponse {
    const lowerMessage = userMessage.toLowerCase();

    // Enhanced rule-based fallback
    if (lowerMessage.includes('add') || lowerMessage.includes('create')) {
      if (lowerMessage.includes('task')) {
        return {
          text: 'I\'ll help you create a new task. Let me open the task creation form for you.',
          confidence: 0.6,
          intent: 'create',
          entities: [{ type: 'item', value: 'task' }],
          actions: ['open_form'],
          suggestions: ['What should the task be called?', 'When is it due?']
        };
      }
      if (lowerMessage.includes('project')) {
        return {
          text: 'I\'ll help you create a new project. Let me open the project creation form for you.',
          confidence: 0.6,
          intent: 'create',
          entities: [{ type: 'item', value: 'project' }],
          actions: ['open_form'],
          suggestions: ['What should the project be called?', 'Who will be the owner?']
        };
      }
    }

    if (lowerMessage.includes('dashboard') || lowerMessage.includes('home')) {
      return {
        text: 'I\'ll take you to the dashboard where you can see an overview of all your projects and activities.',
        confidence: 0.8,
        intent: 'navigate',
        entities: [{ type: 'page', value: 'dashboard' }],
        actions: ['navigate_to_page'],
        suggestions: ['View recent projects', 'Check task status']
      };
    }

    return {
      text: 'I understand you\'re asking about "' + userMessage + '". I can help you navigate, create items, search, and answer questions. Try asking me to "add a new task", "create a project", or "go to dashboard"!',
      confidence: 0.4,
      intent: 'help',
      entities: [],
      actions: [],
      suggestions: ['Add new task', 'Create project', 'Go to dashboard', 'Search projects']
    };
  }

  /**
   * Get contextual suggestions based on current page and recent actions
   */
  getContextualSuggestions(context: ChatContext): string[] {
    const suggestions: string[] = [];

    switch (context.userContext.currentPage) {
      case 'dashboard':
        suggestions.push('Show my projects', 'Create new task', 'View recent activity');
        break;
      case 'projects':
        suggestions.push('Add new project', 'Search projects', 'View my projects');
        break;
      case 'tasks':
        suggestions.push('Add new task', 'Filter tasks', 'View completed tasks');
        break;
      default:
        suggestions.push('Go to dashboard', 'Create project', 'Add task', 'Search');
    }

    return suggestions.slice(0, 4); // Limit to 4 suggestions
  }

  /**
   * Analyze user sentiment and adjust response tone
   */
  analyzeSentiment(message: string): 'positive' | 'negative' | 'neutral' {
    const positiveWords = ['good', 'great', 'excellent', 'awesome', 'thanks', 'thank you', 'helpful'];
    const negativeWords = ['bad', 'terrible', 'awful', 'wrong', 'error', 'problem', 'issue'];

    const lowerMessage = message.toLowerCase();
    const positiveCount = positiveWords.filter(word => lowerMessage.includes(word)).length;
    const negativeCount = negativeWords.filter(word => lowerMessage.includes(word)).length;

    if (positiveCount > negativeCount) return 'positive';
    if (negativeCount > positiveCount) return 'negative';
    return 'neutral';
  }
}
